// Color converter https://github.com/omgovich/colord
import { colord } from 'colord';
import { useEffect, useState } from 'react';
import { RgbaStringColorPicker } from 'react-colorful';

import Input from '../atoms/Input';
import Select from '../atoms/Select';
import './ColorPicker.css';

const modes = {
  hex: 'Hex',
  rgba: 'RGB',
};
type Mode = keyof typeof modes;
const items = Object.entries(modes).map(([value, label]) => ({
  value,
  label,
}));

export default function ColorPicker({
  value,
  onChange,
  presets,
  className,
}: {
  value?: string;
  onChange?: (value: string) => void;
  presets?: string[]; // Only supports hex values for now
  className?: string;
}) {
  const [mode, setMode] = useState<Mode>('hex');
  const [internalColor, setInternalColor] = useState(value || '#B2C4CE');

  // Use the external value if provided, otherwise use internal state
  const color = value !== undefined ? value : internalColor;

  // Control over color value from inside of the ColorPicker component
  const handleChange = (hexString: string) => {
    setInternalColor(hexString);
    onChange?.(hexString);
  };

  // Update internal state only when initializing or when value changes and we don't have onChange handler
  useEffect(() => {
    if (value !== undefined) {
      setInternalColor(value);
    }
  }, [value]);

  const handleRgbChange = (values: { r?: number; g?: number; b?: number }) => {
    const newColor = colord({ ...colord(color).toRgb(), ...values });
    if (newColor.isValid()) handleChange(newColor.toHex());
  };

  const isHuePickerActive =
    presets &&
    presets.findIndex((p) => p.toLowerCase() === color.toLowerCase()) === -1;

  return (
    <div className={`cartwhl ${className}`}>
      {presets && (
        <div className="mb-5 flex gap-2">
          {presets.map((hex) => (
            <button
              key={hex}
              className={`aspect-64/52 w-full max-w-[62px] rounded-lg ${
                hex.toLowerCase() === color.toLowerCase()
                  ? 'shadow-outline-faint dark:shadow-outline-faint-dark cursor-default'
                  : 'hover:shadow-menu cursor-pointer'
              }`}
              style={{ backgroundColor: hex }}
              onClick={() => handleChange(hex)}
            />
          ))}
        </div>
      )}

      <div className={isHuePickerActive ? 'active' : ''}>
        <RgbaStringColorPicker
          color={colord(color).toRgbString()}
          onChange={(rgb) => handleChange(colord(rgb).toHex())}
        />
      </div>

      <div className="flex gap-2">
        <Select
          items={items}
          onValueChange={(value) => setMode(value as Mode)}
          value={mode}
          className="w-24 flex-none"
        />
        {mode === 'hex' && (
          <HexInput
            value={colord(color).toHex()}
            onValueChange={handleChange}
          />
        )}
        {mode === 'rgba' && (
          <>
            <Input
              name="color"
              type="number"
              className="w-full"
              value={colord(color).toRgb().r}
              onChange={(e) => handleRgbChange({ r: parseInt(e.target.value) })}
            />
            <Input
              name="color"
              type="number"
              className="w-full"
              value={colord(color).toRgb().g}
              onChange={(e) => handleRgbChange({ g: parseInt(e.target.value) })}
            />
            <Input
              name="color"
              type="number"
              className="w-full"
              value={colord(color).toRgb().b}
              onChange={(e) => handleRgbChange({ b: parseInt(e.target.value) })}
            />
          </>
        )}
      </div>
    </div>
  );
}

function HexInput({
  value,
  onValueChange,
}: {
  value: string;
  onValueChange: (value: string) => void;
}) {
  const [text, setText] = useState(value);
  const [internalValue, setInternalValue] = useState(value);

  // Only update the text when the external value changes and doesn't match our internal value
  useEffect(() => {
    if (value !== internalValue) {
      setText(value);
      setInternalValue(value);
    }
  }, [value, internalValue]);

  return (
    <Input
      name="color"
      className="w-full"
      value={text}
      onChange={(e) => {
        const newValue = e.target.value;
        setText(newValue);

        if (newValue.length >= 6 && colord(newValue).isValid()) {
          const hexValue = colord(newValue).toHex();
          setInternalValue(hexValue);
          onValueChange(hexValue);
        }
      }}
    />
  );
}
